import { Autocomplete, Box, Button, Card, CardContent, CardHeader, Grid, Link, TextField } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { CrudFilters, getDefaultFilter, HttpError, useGo, useMany } from '@refinedev/core';
import { DeleteButton, List, useAutocomplete, useDataGrid } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { Controller } from 'react-hook-form';

import { MODULE_CONTENT_MAP } from '@attackiq/constants/platform';

import { Module, ModuleContent } from '../../types';

type FilterParams = { search: string; module: Module | null };

const ModuleContentList = () => {
  const { dataGridProps, search, filters } = useDataGrid<ModuleContent, HttpError, FilterParams>({
    onSearch: (params: FilterParams) => {
      const filters: CrudFilters = [];

      filters.push({
        field: 'search',
        value: params.search || undefined,
        operator: 'eq'
      });

      filters.push({
        field: 'module_id',
        value: params.module?.id || undefined,
        operator: 'eq'
      });

      return filters;
    },

    filters: {
      initial: [
        { field: 'search', value: '', operator: 'eq' },
        { field: 'module_id', value: '', operator: 'eq' }
      ]
    }
  });

  const { control, handleSubmit } = useForm<FilterParams, HttpError, FilterParams>({
    defaultValues: {
      search: getDefaultFilter('search', filters, 'eq') || '',
      module: null
    }
  });

  const { autocompleteProps: moduleAutocompleteProps } = useAutocomplete<Module>({
    resource: 'modules',
    pagination: { pageSize: 20 },
    debounce: 400,
    onSearch: searchText => [{ field: 'search', operator: 'eq', value: searchText }]
  });

  const modulesIds = dataGridProps.rows.map((item: ModuleContent) => item.module_id);
  const {
    result: modulesData,
    query: { isLoading: isLoadingModules }
  } = useMany<Module>({
    resource: 'modules',
    ids: [...new Set(modulesIds)],
    queryOptions: {
      enabled: modulesIds.length > 0
    }
  });
  const go = useGo();

  const columns: GridColDef<ModuleContent>[] = [
    {
      field: 'content_type',
      headerName: 'Model',
      renderCell: params => MODULE_CONTENT_MAP[params.row.content_type_model],
      flex: 1
    },
    {
      field: 'content_object__name',
      headerName: 'Name',
      renderCell: params => params.row.content_object?.name || params.row.content_object?.template_name,
      sortable: false,
      flex: 2
    },
    {
      field: 'content_object__description',
      headerName: 'Description',
      renderCell: params => params.row.content_object?.description || params.row.content_object?.template_description,
      sortable: false,
      flex: 2
    },
    {
      field: 'module__display_name',
      headerName: 'Module',
      renderCell: ({ row }) => {
        if (isLoadingModules) return 'Loading...';

        const module = modulesData?.data.find(item => item.id === row.module_id);

        const handleClick = () => {
          go({
            to: {
              resource: 'modules',
              action: 'show',
              id: row.module_id
            }
          });
        };

        return (
          <Link sx={{ cursor: 'pointer' }} onClick={handleClick}>
            {module?.display_name}
          </Link>
        );
      },
      flex: 1
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: ({ row }) => {
        const name = row.content_object?.name || row.content_object?.template_name;
        return (
          <DeleteButton
            recordItemId={row.id}
            hideText
            confirmTitle={'Do you want to delete Module Content for "' + name + '" ?'}
          />
        );
      },
      sortable: false,
      maxWidth: 100
    }
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={3}>
        <Card>
          <CardHeader title="Filters" />
          <CardContent>
            <Box
              component="form"
              autoComplete="off"
              sx={{ display: 'flex', flexDirection: 'column' }}
              onSubmit={handleSubmit(search)}
            >
              <Controller
                name="search"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    label="Name"
                    placeholder="Search by name"
                    margin="normal"
                    fullWidth
                    autoFocus
                  />
                )}
              />

              <Controller
                control={control}
                name="module"
                render={({ field: { onChange, value } }) => (
                  <Autocomplete<Module>
                    {...moduleAutocompleteProps}
                    value={value}
                    onChange={(_, newValue) => onChange(newValue)}
                    getOptionLabel={option => option.display_name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    renderInput={params => (
                      <TextField {...params} label="Module" margin="normal" placeholder="Search modules..." />
                    )}
                  />
                )}
              />

              <Box mt={2}>
                <Button type="submit" variant="contained" fullWidth>
                  Apply
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} lg={9}>
        <List canCreate>
          <DataGridPro {...dataGridProps} filterModel={undefined} columns={columns} pagination />
        </List>
      </Grid>
    </Grid>
  );
};

export default ModuleContentList;
